import { useEffect, useState, useRef } from "react";
import { post } from "../js/post";
import { userData, inputOnEnter, selectedOptic } from "../js/utils";
import "../css/InputSearch.css";

import { transformPatientPicture } from "../support-extra/utils/patients";

export default function InputSearch({
  id,
  size,
  op,
  onSelect,
  link,
  onClickLink,
  tabIndex,
  showCode,
  onChange = null,
  disabled = false,
}) {
  const [optionList, setOptionList] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [contactPanel, setContactPanel] = useState(false);
  const dropdownRef = useRef(null);
  const [listIndex, setListIndex] = useState(-1);
  const optionRefs = useRef([]);
  const searchTimeoutRef = useRef(null);

  const scrollToItem = (index) => {
    const el = optionRefs.current[index];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const handleKeyUp = (event) => {
    switch (event.key) {
      case "Enter":
        if (contactPanel && listIndex >= 0) {
          selectItem(contactList[listIndex]);
        } else {
          inputOnEnter(event);
        }
        setContactPanel(false);
        break;
      case "Escape":
        setContactPanel(false);
        break;
      case "ArrowDown":
        if (contactPanel) {
          let lIndex = listIndex + 1;
          if (lIndex >= contactList.length) {
            lIndex = 0;
          }
          setListIndex(lIndex);
          scrollToItem(lIndex);
        }
        break;
      case "ArrowUp":
        if (contactPanel) {
          let lIndex = listIndex - 1;
          if (lIndex < 0) {
            lIndex = contactList.length - 1;
          }
          setListIndex(lIndex);
          scrollToItem(lIndex);
        }
        break;
    }
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case "Tab":
        if (contactPanel && listIndex >= 0) {
          selectItem(contactList[listIndex]);
        }
        setContactPanel(false);
        break;
    }
  };

  const handleSearchTerm = (event) => {
    const value = event.target.value.toUpperCase();
    setSearchTerm(value);

    if (onChange) {
      onChange(value);
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchContact(value);
    }, 500);
  };

  const searchContact = async (term) => {
    const response = await post("BuscarPersonaContacto.php", {
      token: userData.token,
      optica: selectedOptic.id,
      search: term,
    });

    let patientListAux = response;
    patientListAux.forEach((patient) => {
      patient.fotoPaciente = transformPatientPicture(patient.foto);
    });

    setContactPanel(patientListAux.length > 0);
    setContactList(patientListAux);
  };

  const getComboboxSize = () => {
    let additionalClass = "";
    switch (size) {
      case 1:
        additionalClass = "combobox-small";
        break;
      case 2:
        additionalClass = "combobox-medium";
        break;
      case 3:
        additionalClass = "combobox-large";
        break;
      default:
        break;
    }
    return additionalClass;
  };

  const selectItem = (element) => {
    onSelect(element);
    if (showCode) {
      setSearchTerm(element.codigo);
    } else {
      setSearchTerm(element.nombre + " " + element.apellidos);
    }
    setContactList([]);
    setContactPanel(false);
  };

  useEffect(() => {
    setSearchTerm(op);
  }, [op]);

  useEffect(() => {
    if (contactPanel === false) {
      setListIndex(-1);
    }
  }, [contactPanel]);

  useEffect(() => {
    if (contactList.length > 0 && contactPanel) {
      setListIndex(0);
    }
  }, [contactList, contactPanel]);

  useEffect(() => {
    const refineOptionList = () => {
      const list =
        contactList.length > 0
          ? contactList.map((e, i) => {
              return (
                <div
                  className={`dropdown-op ${
                    i === listIndex ? "op-selected" : ""
                  }`}
                  key={i}
                  ref={(el) => (optionRefs.current[i] = el)}
                  onClick={() => {
                    selectItem(e);
                  }}
                >
                  <img
                    src={e.fotoPaciente}
                    loading="lazy"
                    alt="Patient img"
                    className="op-img"
                  />
                  <div className="op-info">
                    <span>{`${e.nombre} ${e.apellidos} (${e.codigo})`}</span>
                    {e.localidad && (
                      <span className="sub-info">{e.localidad}</span>
                    )}
                    {e.email && e.telefono && (
                      <span className="sub-info">{`${e.email} | ${e.telefono}`}</span>
                    )}
                    {!e.email && e.telefono && (
                      <span className="sub-info">{e.telefono}</span>
                    )}
                    {e.email && !e.telefono && (
                      <span className="sub-info">{e.email}</span>
                    )}
                  </div>
                </div>
              );
            })
          : [
              <div className="dropdown-op" key={0}>
                <span className="">No options</span>
              </div>,
            ];
      setOptionList(list);
    };

    refineOptionList();
  }, [contactList, listIndex, onSelect]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setContactPanel(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`input-search combobox ${getComboboxSize()}`}
      ref={dropdownRef}
    >
      <div
        className={`dropdown ${contactList.length > 0 ? "dropdown-open" : ""} ${
          disabled ? "inactive-bkg" : ""
        }`}
      >
        <input
          id={id}
          tabIndex={tabIndex}
          type="text"
          value={searchTerm}
          className="input-searchterm"
          onChange={handleSearchTerm}
          onKeyUp={(e) => handleKeyUp(e)}
          onKeyDown={(e) => handleKeyDown(e)}
          autoComplete="off"
          readOnly={disabled}
        ></input>
        <div className="downicon">
          <img
            src={
              contactList.length > 0
                ? "/images/close-dark.svg"
                : "/images/search-blue.svg"
            }
            loading="lazy"
            alt="Panel icon"
            className={`vectors-wrapper-11 ${
              contactList.length > 0 ? "button-op" : ""
            }`}
            onClick={() => {
              if (contactList.length > 0) {
                setContactList([]);
                setContactPanel(false);
                setSearchTerm("");
              }
            }}
          />
        </div>
        {link && (
          <img
            className="button-op"
            alt="Icon"
            src="/images/link.svg"
            onClick={() => {
              onClickLink();
            }}
          ></img>
        )}
      </div>
      {contactList.length > 0 && contactPanel === true && (
        <div className="dropdown-options">{optionList}</div>
      )}
    </div>
  );
}
